import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import moment from 'moment-timezone';
import { formatUptime } from '../Health/model';
import { rowStateLine, rowNow, rowFacts } from '../../screenRowModel';
import { IcPlay, IcPause, IcPush, IcDown, IcGrip, IcWarn, IcOff, IcCheck } from './icons';

const STATE_ICON = { off: <IcOff />, warn: <IcWarn />, pause: <IcPause />, '': null };

/** "10 ساعات و56 دقيقة" -- minutes since the realtime first_login, in words, refreshed every minute (same formatUptime as WorkingTimer). */
export function useUptimeText(firstLogin, enabled) {
  const { t } = useTranslation();
  const [text, setText] = useState('');
  useEffect(() => {
    if (!enabled || !firstLogin) { setText(''); return undefined; }
    const at = moment.utc(firstLogin).local();
    const tick = () => setText(formatUptime(t, Math.floor(moment.duration(moment().diff(at)).asMinutes()), t('devices.group_just_now')));
    tick();
    const id = setInterval(tick, 60000);
    return () => clearInterval(id);
  }, [firstLogin, enabled, t]);
  return text;
}

/**
 * One screen as a LIST row (the prototype's row + its expandable detail strip). Presentational: every value comes from DeviceCard, which owns the
 * realtime listeners, the optimistic play state and the dialogs -- so the row can never disagree with the card.
 */
export default function ScreenRow({ data, row, nowLabel, firstLogin, expanded, onToggle, onPush, onTogglePlay, onOpen, onReport, pushTitle, handleProps, dragging }) {
  const { t } = useTranslation();
  const uptimeText = useUptimeText(firstLogin, row.status === 'online');
  const isOff = row.status === 'offline'; const isWarn = !isOff && row.sev === 'warn';
  // tell the group header what THIS row resolved, so «N تحتاج انتباهًا» can never disagree with the rows underneath it
  useEffect(() => { if (onReport) onReport(data.id, { off: isOff, warn: isWarn }); }, [onReport, data.id, isOff, isWarn]);
  const line = rowStateLine(row, t, { data, uptimeText });
  const now = rowNow(row, nowLabel);
  const sevCls = row.sev === 'ok' ? '' : ` pp-ls-${row.sev}`;
  const canToggle = row.status === 'online' && row.playing !== null && row.playing !== undefined;
  const stop = (fn) => (e) => { e.stopPropagation(); fn(); };
  return (
    <div
      className={`pp-ls-row${sevCls}${dragging ? ' pp-ls-dragging' : ''}`}
      data-id={data.id}
      data-testid="screen-row"
      tabIndex={0}
      role="button"
      aria-expanded={expanded}
      onClick={(e) => { if (e.target.closest('button, .pp-ls-handle, [data-stop]')) return; onToggle(); }}
      onKeyDown={(e) => { if ((e.key === 'Enter' || e.key === ' ') && e.target === e.currentTarget) { e.preventDefault(); onToggle(); } }}
    >
      <span className="pp-ls-handle" {...(handleProps || {})} aria-label={t('screens_list.handle_title')} title={t('screens_list.handle_title')}><IcGrip /></span>
      <div className="pp-ls-name"><span className={`pp-ls-dot pp-ls-${row.dot}`} /><b>{data.name || '---'}</b></div>
      <div className="pp-ls-now" data-testid="row-now">
        {now.kind === 'off' && <span>—</span>}
        {now.kind === 'playing' && <><span className="pp-ls-pl"><IcPlay /></span><span>{now.label}</span></>}
        {now.kind === 'paused' && <><IcPause /><span>{now.label}</span></>}
        {now.kind === 'none' && <span>{t('screens_list.now_none')}</span>}
      </div>
      <div className={`pp-ls-state pp-ls-${line.cls}`} data-testid="row-state">{STATE_ICON[line.icon]}<span className="pp-ls-t">{line.text}</span></div>
      <div className="pp-ls-ract">
        {canToggle && (
          <button type="button" className="pp-ls-btn pp-ls-sm pp-ls-icon" data-testid="row-toggle" aria-label={row.playing ? t('screens_list.toggle_pause') : t('screens_list.toggle_play')} onClick={stop(onTogglePlay)}>
            {row.playing ? <IcPause /> : <IcPlay />}
          </button>
        )}
        <button type="button" className="pp-ls-btn pp-ls-sm" data-testid="row-push" title={pushTitle} onClick={stop(onPush)}><IcPush /><span className="pp-ls-push-t">{t('devices.action_push_content')}</span></button>
        <span className="pp-ls-go"><IcDown /></span>
      </div>
      {expanded && (
        <div className="pp-ls-detail" data-stop="1" data-testid="row-detail">
          <div className="pp-ls-facts">
            {rowFacts(row, t, { data, uptimeText }).map((f, i) => (
              <span key={i} className={`pp-ls-fact${f.cls ? ` pp-ls-${f.cls}` : ''}`}>{f.icon === 'check' ? <IcCheck /> : f.icon === 'off' ? <IcOff /> : null}{f.text}</span>
            ))}
          </div>
          <div className="pp-ls-dact">
            <button type="button" className="pp-ls-btn pp-ls-sm" title={pushTitle} onClick={stop(onPush)}><IcPush /> {t('devices.action_push_content')}</button>
            <button type="button" className="pp-ls-btn pp-ls-sm pp-ls-primary" data-testid="row-open" onClick={stop(onOpen)}>{t('screens_list.open_page')}</button>
          </div>
        </div>
      )}
    </div>
  );
}
